import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/common/jwt.service";
import { API_URL, x_api_key } from "@/common/config";

const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = API_URL;
    Vue.axios.defaults.headers.common["x-api-key"] = x_api_key;
  },

  setHeader() {
    Vue.axios.defaults.headers.common[
      "Authorization"
    ] = `${JwtService.getToken()}`;
  },

  query(resource, params) {
    return Vue.axios.get(resource, params).catch(error => {
      throw new Error(`ApiService ${error}`);
    });
  },

  get(resource, slug = "") {
    return Vue.axios.get(`${resource}?${slug}`).catch(error => {
      throw new Error(`ApiService ${error}`);
    });
  },

  post(resource, params) {
    return Vue.axios.post(`${resource}`, params);
  },

  update(resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params);
  },

  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },

  delete(resource) {
    return Vue.axios.delete(resource).catch(error => {
      throw new Error(`ApiService ${error}`);
    });
  }
};

export default ApiService;

export const SessionsService = {
  get() {
    return ApiService.get("/sessions/current");
  },
  getCurrentApplication(id = "NULL") {
    return ApiService.get(`/application/current/${id}`);
  },
  creatApplication(type = "") {
    return ApiService.post(`/application/${type}`);
  },
  getApplication(id = "") {
    return ApiService.get("/application", id);
  },
  deleteApplication(id) {
    return ApiService.delete(`/application/${id}`);
  },
  criteriaFormGet(id) {
    return ApiService.get(`/application/${id}/form_criteria`);
  },
  innovationsFormGet(id) {
    return ApiService.get(`/application/${id}/innovations`);
  },
  startupInformationsFormGet(id) {
    return ApiService.get(`/application/startup-informations/${id}`);
  },
  checklistGet(id) {
    return ApiService.get(`/application/checklist/${id}`);
  },
  criteriaFormSaveDraft(id = "", from) {
    return ApiService.post(`/application/${id}/form_criteria`, {
      responses: from
    });
  },
  startupInformationsFormSave(id, form) {
    return ApiService.post(`/application/startup-informations/${id}/save`, {
      form: form
    });
  },
  innovationsFormSaveDraft(id = "", from) {
    return ApiService.post(`/application/${id}/innovations`, {
      responses: from
    });
  },
  updateStatusApplication(applicationId = "") {
    return ApiService.post(`/application/updateStatus/${applicationId}`);
  },
  // Get list applications by user
  getListApplications() {
    return ApiService.get("/application/all");
  },
  // Check apply multiple applications
  checkApplyMultipleApplications(userId = "") {
    return ApiService.post(`/application/check_apply/${userId}`);
  },
  checkLabel(hashId) {
    return ApiService.get(
      `/api/requests/check_label/${hashId}`
    );
  }
};
export const TeamsService = {
  getAll(id) {
    return ApiService.get(`/application/${id}/teams`);
  },
  get(id, memberId, ecosystem) {
    ecosystem = ecosystem ? '/ecosystem' : '';
    return ApiService.get(`/application/${id}/teams/view_member/${memberId}${ecosystem}`);
  },
  post(id, payload) {
    return ApiService.post(`/application/${id}/teams/add_member`, payload);
  },
  put(id, memberId, payload, ecosystem) {
    ecosystem = ecosystem ? '/ecosystem' : '';
    return ApiService.post(
      `/application/${id}/teams/edit_member/${memberId}${ecosystem}`,
      payload
    );
  },
  destroy(id, memberId, ecosystem) {
    ecosystem = ecosystem ? '/ecosystem' : '';
    return ApiService.delete(
      `/application/${id}/teams/delete_member/${memberId}${ecosystem}`
    );
  },
  makeLead(id, memberId) {
    return ApiService.post(`/application/${id}/teams/lead/${memberId}`);
  },
  sendInvitationToMember(id, email, resend) {
    return ApiService.post(`/application/${id}/send_invitation`, {
      email: email,
      resend: resend
    });
  }
};
export const RequestService = {
  getApplication(id = "") {
    return ApiService.get("/application", id);
  },
  requestFormGet(label, type_id) {
    return ApiService.get(`/api/request/show/${label}/${type_id}`);
  },
  requestFormGetDetail(label, type_id, request_id) {
    return ApiService.get(
      `/api/request/show-detail/${label}/${type_id}/${request_id}`
    );
  },
  requestFormSaveDraft(label, application_id, type_id, member_id, from) {
    let uri = label ?
      `${application_id}/${type_id}/${label}` :
      `${application_id}/${type_id}`;
    return ApiService.post(
      `/api/request/add/${uri}`,
      {
        responses: from,
        member_id: member_id
      }
    );
  },
  declarationFormSave(form, formName) {
    return ApiService.post(
      `/api/declaration/add/${formName}/${form.id}`,
      {
        responses: form
      }
    );
  },
  requestFormUpdate(label, type_id, request_id, from) {
    return ApiService.post(
      `/api/request/update/${label}/${type_id}/${request_id}`,
      {
        responses: from
      }
    );
  },
  prelabelFormUpdate(form) {
    return ApiService.post(
      `/api/request/update-request`,
      {
        responses: form
      }
    );
  },
  companySave(form, app_id) {
    return ApiService.post(`/companies/save/${app_id}`, form);
  },
  requestLabelGet(type_id) {
    return ApiService.get(`/api/request/label/${type_id}`);
  },
  getCurrentRequests(id, code_label) {
    return ApiService.get(`/api/request/current_requests/${id}/${code_label}`);
  },
  getCompletedRequests(id, code_label) {
    return ApiService.get(
      `/api/request/completed_requests/${id}/${code_label}`
    );
  },
  requestTeamGet(label, type_id, team) {
    return ApiService.get(`/api/request/show-team/${label}/${type_id}/${team}`);
  },
  // Add comment request
  commentRequest(label, type_id, request_id, comment, documentId) {
    return ApiService.post(
      `/api/request/add-comment-request/${label}/${type_id}/${request_id}`,
      {
        comment: comment,
        document_id: documentId
      }
    );
  },
  // List of default requests Declaration
  getListRequestsDeclarations(type) {
    return ApiService.get(`/api/requests-declarations/${type}`);
  },
  // List of Investors Requests
  getInvestorsListDeclarations(type) {
    return ApiService.get(`/api/investors-declarations/${type}`);
  },
  // check validated Investors Requests
  checkInvestorsDeclarations(id) {
    return ApiService.get(`/api/check-investors-declarations/${id}`);
  },
  // List of current requests Declaration
  getCurrentRequestsDeclarations(id) {
    return ApiService.get(`/api/request/current-requests-declarations/${id}`);
  },
  // List of completed requests Declaration
  getCompletedRequestsDeclarations(id) {
    return ApiService.get(`/api/request/completed-requests-declarations/${id}`);
  },
  requestDeclarationFormGetDetail(appType, formName, hashId) {
    return ApiService.get(
      `/api/request/show-detail-declaration/${appType}/${formName}/${hashId}`
    );
  },
  requestDeclarationFormUpdate(appType, formName,hashId, from) {
    let uri = `${appType}/${formName}/${hashId}`;
    return ApiService.post(
      `/api/request/update-declaration/${uri}`,
      {
        responses: from
      }
    );
  },
  // Add comment declaration
  commentDeclaration(type, form_code, request_id, comment, documentId) {
    let uri = type ?
      `${type}/${form_code}/${request_id}` :
      `${form_code}/${request_id}`;
    return ApiService.post(
      `api/request/add-comment-declaration/${uri}`,
      {
        comment: comment,
        document_id: documentId
      }
    );
  },
  deleteFile(payload) {
    return ApiService.post(
      `api/docs/remove`,
      {
        questionId: payload.questionId,
        documentId: payload.documentId,
        requestId: payload.requestId
      }
    );
  },
  declarationFormGet(appType, formName, hashId) {
    return ApiService.get(`/api/declaration/show/${appType}/${formName}/${hashId}`);
  },
};
export const EcosystemService = {
  getEcosystemApplications(userId) {
    return ApiService.get(`/ecosystem/user-application/${userId}`);
  },

  getEcosystemLabelRefs(label_ref) {
    return ApiService.get(`/ecosystem/verify-label-ref/${label_ref}`);
  },

  createEcosystemApplication(type) {
    return ApiService.post(`/ecosystem/application/create/${type}`);
  },

  getEcosystemFormData(type, formName, hashId) {
    return ApiService.get(`/ecosystem/form/${type}/${formName}/${hashId}`);
  },

  deleteEcosystemApplication(id) {
    return ApiService.delete(`/ecosystem/application/${id}`);
  },

  saveEcosystemForm(form, formName) {
    return ApiService.post(`/ecosystem/form/${formName}/${form.id}`, {
      responses: form.responses
    });
  },

  getEcosystemAppChecklist(hashId) {
    return ApiService.get(`/ecosystem/checklist/${hashId}`);
  },

  fetchEcosystemApp(hashId) {
    return ApiService.get(`/ecosystem/fetch/${hashId}`);
  },

  getStructureAndOrganization(id) {
    return ApiService.get(`/ecosystem/application/${id}/structure_and_organization`);
  },

  updateStatusEcosystemApp(applicationId = "") {
    return ApiService.post(`/ecosystem/application/update-status/${applicationId}`);
  },
  
  getEcosystemTeam(hashId = "") {
    return ApiService.get(`/ecosystem/team/${hashId}`);
  },

  sendInvitation(payload) {
    return ApiService.post(`/ecosystem/team/${payload.hashId}/send-invitation`, {
      email: payload.email,
      resend: payload.resend
    });
  },

  getBuilderForms() {
    return ApiService.get(`/ecosystem/form-builder/forms`);
  },

  getBuilderFormData(id) {
    return ApiService.get(`/ecosystem/form-builder/form/${id}`);
  },

  // Investor routes
  saveInvestorForm(form) {
    return ApiService.post(`/ecosystem/investor/save-form`, {
      data: form.data,
    });
  },

  fetchInvestorForm(id) {
    return id ?
      ApiService.get(`/ecosystem/investor/form-data/${id}`) :
      ApiService.get(`/ecosystem/investor/form-data`);
  },

  getInvestorDeclaration(id) {
    return ApiService.get(`/ecosystem/investor/certificate/${id}`);
  },

  downloadInvestorDeclaration(code) {
    return ApiService.get(`/ecosystem/investor/download/${code}`);
  },

  /************************/
  /*** Sessionless Routes **/
  /************************/  
  fetchSessionlessAppByAppType(type) {
    return ApiService.get(`/ecosystem/application/${type}`);
  },

};